import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { AlertTriangle } from "lucide-react";
import { api, fmtError } from "@/lib/api";
import { METIERS, CERTIFS, L, showsTarifMensuel } from "@/constants/labels";
import { LogoEditor, PhotosEditor } from "@/components/ProfileMediaEditor";

export default function MonProfil() {
  const [p, setP] = useState({
    metier: "", specialite: "", sous_specialite: "", description: "",
    tarif_horaire: "", tarif_journalier: "", tarif_mensuel_ht: "",
    ville: "", code_postal: "", rayon_km: "",
    certifications: [], portfolio_urls: [],
    equipment_codes: [], equipment_autre: "", vehicules: [], permis_remorque: false,
  });
  const [busy, setBusy] = useState(false);
  const [portfolioUrl, setPortfolioUrl] = useState("");
  const [logoUrl, setLogoUrl] = useState(null);
  const [equipmentCatalog, setEquipmentCatalog] = useState([]); // [{categorie, items:[{code,label}]}]
  const [vehiclesCatalog, setVehiclesCatalog] = useState([]);   // [{code,label}]

  const load = useCallback(async () => {
    const [prof, eq, veh] = await Promise.all([
      api.get("/prestataires/me/profile"),
      api.get("/equipment/catalog"),
      api.get("/vehicles/catalog"),
    ]);
    const d = prof.data || {};
    setLogoUrl(d.logo_url || null);
    delete d.logo_url;
    setP((prev) => ({
      ...prev,
      ...d,
      certifications: d.certifications || [],
      portfolio_urls: d.portfolio_urls || [],
      equipment_codes: d.equipment_codes || [],
      equipment_autre: d.equipment_autre || "",
      vehicules: d.vehicules || [],
      permis_remorque: !!d.permis_remorque,
    }));
    setEquipmentCatalog(eq.data || []);
    setVehiclesCatalog(veh.data || []);
  }, []);
  useEffect(() => { load(); }, [load]);

  const specs = p.metier && METIERS[p.metier] ? Object.keys(METIERS[p.metier]) : [];
  const sspecs = p.metier && p.specialite && METIERS[p.metier]?.[p.specialite] ? METIERS[p.metier][p.specialite] : [];

  const toggleCert = (c) => setP({ ...p, certifications: p.certifications.includes(c) ? p.certifications.filter(x => x !== c) : [...p.certifications, c] });
  const addPortfolio = () => { if (portfolioUrl) { setP({ ...p, portfolio_urls: [...p.portfolio_urls, portfolioUrl] }); setPortfolioUrl(""); } };
  const removePortfolio = (i) => setP({ ...p, portfolio_urls: p.portfolio_urls.filter((_, k) => k !== i) });
  const toggleEquipment = (code) => setP({ ...p, equipment_codes: p.equipment_codes.includes(code) ? p.equipment_codes.filter(c => c !== code) : [...p.equipment_codes, code] });
  const toggleVehicle = (code) => setP({ ...p, vehicules: p.vehicules.includes(code) ? p.vehicules.filter(c => c !== code) : [...p.vehicules, code] });

  const save = async (e) => {
    e.preventDefault(); setBusy(true);
    try {
      const payload = {
        ...p,
        tarif_horaire: p.tarif_horaire ? Number(p.tarif_horaire) : null,
        tarif_journalier: p.tarif_journalier ? Number(p.tarif_journalier) : null,
        tarif_mensuel_ht: p.tarif_mensuel_ht ? Number(p.tarif_mensuel_ht) : null,
        rayon_km: p.rayon_km ? Number(p.rayon_km) : null,
        equipment_autre: p.equipment_autre || null,
      };
      await api.put("/prestataires/me", payload);
      toast.success("Profil enregistré");
    } catch (err) { toast.error(fmtError(err)); } finally { setBusy(false); }
  };

  const Pill = ({ active, onClick, testid, children }) => (
    <button type="button" onClick={onClick} data-testid={testid}
      className={`px-3 py-1 border rounded-[4px] text-sm ${active ? "bg-[#1B3A5C] text-white border-[#1B3A5C]" : "bg-white border-[#CBD5E1] text-[#1F2933]"}`}>
      {children}
    </button>
  );

  return (
    <form onSubmit={save} className="max-w-3xl">
      <h1 className="text-2xl md:text-3xl font-semibold text-[#1F2933]">{L.wording.monProfilH1}</h1>
      <div className="mt-6 space-y-4">
        <div className="grid md:grid-cols-3 gap-3">
          <div>
            <label className="label">Corps de métier</label>
            <select className="select" value={p.metier} onChange={(e) => setP({ ...p, metier: e.target.value, specialite: "", sous_specialite: "" })} data-testid="profil-metier">
              <option value="">Sélectionner</option>
              {Object.keys(METIERS).map(m => <option key={m}>{m}</option>)}
            </select>
          </div>
          <div>
            <label className="label">Spécialité</label>
            <select className="select" value={p.specialite} onChange={(e) => setP({ ...p, specialite: e.target.value, sous_specialite: "" })} disabled={!p.metier} data-testid="profil-specialite">
              <option value="">Sélectionner</option>
              {specs.map(s => <option key={s}>{s}</option>)}
            </select>
          </div>
          <div>
            <label className="label">Sous-spécialité</label>
            <select className="select" value={p.sous_specialite} onChange={(e) => setP({ ...p, sous_specialite: e.target.value })} disabled={!p.specialite} data-testid="profil-sous-specialite">
              <option value="">Sélectionner</option>
              {sspecs.map(s => <option key={s}>{s}</option>)}
            </select>
          </div>
        </div>

        <div>
          <label className="label">Présentation</label>
          <textarea className="textarea" value={p.description || ""} onChange={(e) => setP({ ...p, description: e.target.value })} rows={4} data-testid="profil-description" />
        </div>

        <div className={`grid ${showsTarifMensuel(p.metier) ? "md:grid-cols-3" : "md:grid-cols-2"} gap-3`}>
          <div>
            <label className="label">Tarif horaire HT (€)</label>
            <input type="number" step="0.01" min="0" className="input" value={p.tarif_horaire || ""} onChange={(e) => setP({ ...p, tarif_horaire: e.target.value })} data-testid="profil-tarif-h" />
          </div>
          <div>
            <label className="label">Tarif journalier HT (€)</label>
            <input type="number" step="0.01" min="0" className="input" value={p.tarif_journalier || ""} onChange={(e) => setP({ ...p, tarif_journalier: e.target.value })} data-testid="profil-tarif-j" />
          </div>
          {showsTarifMensuel(p.metier) && (
            <div>
              <label className="label">Tarif mensuel HT (€) <span className="text-xs text-[#3E4C59] font-normal">— location</span></label>
              <input type="number" step="0.01" min="0" className="input"
                value={p.tarif_mensuel_ht || ""}
                onChange={(e) => setP({ ...p, tarif_mensuel_ht: e.target.value })}
                placeholder="ex. 850"
                data-testid="profil-tarif-m" />
            </div>
          )}
        </div>

        <div className="grid md:grid-cols-3 gap-3">
          <div>
            <label className="label">Ville</label>
            <input className="input" value={p.ville || ""} onChange={(e) => setP({ ...p, ville: e.target.value })} data-testid="profil-ville" />
          </div>
          <div>
            <label className="label">Code postal</label>
            <input pattern="\d{5}" className="input" value={p.code_postal || ""} onChange={(e) => setP({ ...p, code_postal: e.target.value })} data-testid="profil-cp" />
          </div>
          <div>
            <label className="label">Rayon d'intervention (km)</label>
            <input type="number" min="0" className="input" value={p.rayon_km || ""} onChange={(e) => setP({ ...p, rayon_km: e.target.value })} data-testid="profil-rayon" />
          </div>
        </div>

        <div>
          <label className="label">Certifications</label>
          <div className="flex flex-wrap gap-2">
            {CERTIFS.map(c => (
              <Pill key={c} active={p.certifications.includes(c)} onClick={() => toggleCert(c)} testid={`certif-${c}`}>{c}</Pill>
            ))}
          </div>
        </div>

        {/* --- Matériel et outillage --- */}
        <div className="pt-4 border-t border-[#E4E7EB]">
          <label className="label">Matériel et outillage <span className="font-normal text-[#3E4C59]">— cochez ce dont vous disposez</span></label>
          <div className="flex items-start gap-2 mt-1 mb-3 p-2 bg-[#FFF7ED] border-l-4 border-[#E8710A] text-xs text-[#7C3E00] leading-relaxed" data-testid="equipment-warning">
            <AlertTriangle size={16} className="shrink-0 mt-0.5" />
            <span>
              Cette section décrit le matériel que <strong>vous possédez pour votre propre usage</strong> sur chantier.
              Elle n'a rien à voir avec les prestations que vous vendez : cocher « échafaudage roulant » dans
              <em> Élévation et accès</em> ne signifie pas que vous montez des échafaudages pour d'autres — c'est
              simplement que vous en avez un pour bosser en hauteur. Si vous <strong>montez / louez</strong> de
              l'échafaudage comme prestation, sélectionnez le corps de métier <em>10000 — Pose, montage et location
              d'échafaudages</em> ci-dessus.
            </span>
          </div>
          <div className="space-y-3">
            {equipmentCatalog.map(cat => (
              <div key={cat.categorie} data-testid={`equipment-cat-${cat.categorie}`}>
                <div className="text-xs uppercase tracking-wider text-[#3E4C59] font-semibold mb-1.5">{cat.categorie}</div>
                <div className="flex flex-wrap gap-1.5">
                  {cat.items.map(it => (
                    <Pill key={it.code} active={p.equipment_codes.includes(it.code)}
                      onClick={() => toggleEquipment(it.code)}
                      testid={`equipment-${it.code}`}>{it.label}</Pill>
                  ))}
                </div>
              </div>
            ))}
          </div>
          <div className="mt-3">
            <label className="label">Autre matériel (texte libre)</label>
            <input className="input" maxLength={500} placeholder="ex. compresseur haute pression, plieuse à zinc…"
              value={p.equipment_autre || ""}
              onChange={(e) => setP({ ...p, equipment_autre: e.target.value })}
              data-testid="equipment-autre" />
          </div>
        </div>

        {/* --- Véhicules --- */}
        <div className="pt-4 border-t border-[#E4E7EB]">
          <label className="label">Véhicules</label>
          <div className="flex flex-wrap gap-1.5">
            {vehiclesCatalog.map(v => (
              <Pill key={v.code} active={p.vehicules.includes(v.code)}
                onClick={() => toggleVehicle(v.code)}
                testid={`vehicle-${v.code}`}>{v.label}</Pill>
            ))}
          </div>
          <label className="inline-flex items-center gap-2 text-sm mt-3">
            <input type="checkbox" checked={!!p.permis_remorque}
              onChange={(e) => setP({ ...p, permis_remorque: e.target.checked })}
              data-testid="permis-remorque" />
            Je possède le permis remorque / BE
          </label>
        </div>

        <LogoEditor logoUrl={logoUrl} onChange={setLogoUrl} />
        <PhotosEditor />

        <div className="pt-4 border-t border-[#E4E7EB]">
          <label className="label">Liens externes <span className="font-normal text-[#3E4C59]">— site, Instagram, photos hébergées ailleurs</span></label>
          <div className="flex gap-2">
            <input className="input" placeholder="https://…" value={portfolioUrl} onChange={(e) => setPortfolioUrl(e.target.value)} data-testid="profil-portfolio-input" />
            <button type="button" onClick={addPortfolio} className="btn-outline" data-testid="profil-portfolio-add">Ajouter</button>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mt-2">
            {p.portfolio_urls.map((u, i) => (
              <div key={i} className="relative">
                <img src={u} alt="" className="w-full h-24 object-cover border border-[#E4E7EB] rounded-[4px]" />
                <button type="button" onClick={() => removePortfolio(i)} className="absolute top-1 right-1 bg-white/95 border border-[#CBD5E1] text-xs px-2 py-0.5 rounded-[4px]">Retirer</button>
              </div>
            ))}
          </div>
        </div>

        <button disabled={busy} className="btn-primary" data-testid="profil-save">{busy ? "…" : L.common.save}</button>
      </div>
    </form>
  );
}
